import React, { useState } from 'react';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Cell, LabelList } from 'recharts';

// Define subcategories for each main category
const subcategories = {
  Auditiv: {
    'ba_aud_01_1': 'Geräusche',
    'ba_aud_01_2': 'Lautstärke', 
    'ba_aud_01_3': 'Töne von Geräten',
    'textentry_aud': 'freie Textantwort' 
  },
  Visuell: {
    'ba_vis_01_1': 'Bewegungen',
    'ba_vis_01_2': 'Muster',
    'ba_vis_01_3': 'Spiegelungen',
    'ba_vis_01_4': 'helles Licht',
    'ba_vis_01_5': 'künstliches Licht',
    'ba_vis_01_6': 'Farben',
    'textentry_vis': 'freie Textantwort' 
  },
  Taktil: {
    'ba_tak_01_1': 'Vibration/Erschütterung',   
    'ba_tak_01_2': 'Temperatur',
    'ba_tak_01_3': 'Wind',
    'ba_tak_01_4': 'Persönliche Distanz/Nähe',
    'ba_tak_01_5': 'Berührungsempfinden',
    'textentry_taktil': 'freie Textantwort' 
  },
  Geruch_Geschmack: {
    'ba_ge_01_1': 'Geruch',
    'ba_ge_01_2': 'Geschmack',
    'textentry_geruch': 'freie Textantwort'
  },
  Unvorhersehbarkeit: {
    'ba_unp_01_1': 'Neues/Unbekanntes',
    'ba_unp_01_2': 'Raumwechsel',
    'ba_unp_01_3': 'anderes Aussehen',
    'ba_unp_01_4': 'Ordnung nicht eingehalten',
    'textentry_unp': 'freie Textantwort'
  },
  Sonstiges: {
    'ba_so_01_1': 'andere Kinder oder Lehrkräfte', 
    'ba_so_01_2': 'Zeitdruck',
    'ba_so_01_3': 'zu viel gleichzeitig los',
    'ba_so_01_4': 'Bodenbeschaffenheit',
    'ba_so_01_5': 'unterbrochene Handlungen',
    'textentry_so': 'freie Textantwort'
  }
};

// Function to generate color variations based on main category color
const getSubcategoryColors = (mainCategory, count) => {
  const baseColors = {
    Auditiv: '#F29400',
    Geruch_Geschmack: '#005B9A',
    Taktil: '#EB6A27',
    Visuell: '#AF122C',
    Unvorhersehbarkeit: '#37A42C',
    Sonstiges: '#646567'
  };
  
  const baseColor = baseColors[mainCategory];
  const colors = [];
  
  // Convert hex to RGB
  const hexToRgb = (hex) => {
    const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
    return result ? {
      r: parseInt(result[1], 16),
      g: parseInt(result[2], 16),
      b: parseInt(result[3], 16)
    } : { r: 0, g: 0, b: 0 };
  };
  
  // Convert RGB to hex
  const rgbToHex = (r, g, b) => {
    return `#${((1 << 24) + (r << 16) + (g << 8) + b).toString(16).slice(1)}`;
  };
  
  const rgb = hexToRgb(baseColor);
  
  // Generate color variations for each subcategory
  for (let i = 0; i < count; i++) {
    // Create variations by adjusting brightness (darker to lighter)
    const factor = 0.6 + (i * 0.3); // Creates a range from darker to lighter
    const r = Math.min(255, Math.floor(rgb.r * factor));
    const g = Math.min(255, Math.floor(rgb.g * factor));
    const b = Math.min(255, Math.floor(rgb.b * factor));
    
    colors.push(rgbToHex(r, g, b));
  }
  
  return colors;
};

const SubcategoryBarChart = ({ responses }) => {
  const [selectedCategory, setSelectedCategory] = useState('');
  
  const processData = () => {
    if (!selectedCategory) return [];
    
    const categorySubs = subcategories[selectedCategory];
    const subcategoryData = [];
    
    // Count responses for each subcategory
    Object.entries(categorySubs).forEach(([itemId, itemName]) => {
      let count = 0;

      responses.forEach(response => {
      if (response.item_name === itemId) {
        // Handle textentry items differently
        if (itemId.startsWith('textentry_')) {
          // For textentry items, count if Antwort-1 has any text content
          if (response.item_resp && response.item_resp.trim() !== '') {
            count += 1;
          }
        } else {
          // Handle both response formats
          let antwort1, antwort2;
          if (response.item_resp.includes(';')) {
            [antwort2, antwort1] = response.item_resp.split(';');
          } else {
            antwort1 = response.item_resp;
          }
          
          // Only count if Antwort-1 is "1"
          if (antwort1?.trim() === '1' || antwort2?.trim() === '1') {
            count += 1;
          }
        }
      }
    });
      
      subcategoryData.push({
        name: itemName,
        count: count,
        id: itemId
      });
    });
    
    return subcategoryData;
  };
  
  const chartData = processData();
  const colors = getSubcategoryColors(selectedCategory, chartData.length);
  
  return (
    //<div style={{ width: '100%', height: 400, marginTop: '40px' }}>
    <div style={{ width: '100%', marginTop: '30px' }}>
      <h4>Häufigkeit der einzelnen Barrieren je Kategorie über den Teilnahmezeitraum</h4>
      
      <div style={{ marginBottom: '60px' }}>
        <select 
          value={selectedCategory} 
          onChange={(e) => setSelectedCategory(e.target.value)}
          style={{ padding: '8px', fontSize: '16px' }}
        >
          <option value="">wählen sie eine Kategorie</option>
          {Object.keys(subcategories).map(category => (
            <option key={category} value={category}>
              {category.replace('_', '/')}
            </option>
          ))}
        </select>
      </div>
      
      {selectedCategory && chartData.length > 0 && (
        <ResponsiveContainer width="100%" height={300}>
          <BarChart
            data={chartData}
            margin={{ top: 10, right: 30, left: 50, bottom: 100 }}
          >
            <CartesianGrid strokeDasharray="3 3" />
            <XAxis 
              dataKey="name" 
              angle={-45} 
              textAnchor="end" 
              height={70} 
            />
            <YAxis 
              //label={{ value: 'Häufigkeit', angle: -90, position: 'insideLeft' }} 
              domain={[0, 'dataMax + 1']}
              allowDecimals={false}
            />
            <Tooltip 
              formatter={(value) => [value, 'Anzahl']}
              labelFormatter={(name) => `${name}`}
            />
            
            <Bar 
              dataKey="count" 
              //name={`${selectedCategory.replace('_', '/')} Subkategorien`}
            >
              {/* Add different colors for each data point */}
              {chartData.map((entry, index) => (
                <Cell key={`cell-${index}`} fill={colors[index]} />
              ))}
              {/* Add labels on top of each bar */}
              <LabelList dataKey="count" position="top" fill="#000000" />
            </Bar>
          </BarChart>
        </ResponsiveContainer>
      )}
      
      {selectedCategory && chartData.length === 0 && (
        <p>Keine Daten für {selectedCategory.replace('_', '/')} gefunden.</p>
      )}
    </div>
  );
};

export default SubcategoryBarChart;